import {
  AccessLevel,
  BecomeAMemberCombination as BecomeAMemberCombinationTypes,
  BecomeAMemberVoucherCategory,
  MembershipType,
} from '@/generated/contentfulTypes';

import { BecomeAMemberCombination } from '@/generated/fragments/BecomeAMemberCombination';
import { CombinationFlowTypes, Languages } from './constants';
import { BecomeAMemberVoucherCategory as BecomeAMemberVoucherCategoryFragment } from '@/generated/fragments';
import { Maybe } from 'graphql/jsutils/Maybe';
import { fetchContentfulContentModelData } from './fetchContentfulContentModelData';
import { BecomeAMemberCombinationFetchingTypes, CombinationAccessLevel, CombinationLabelGroup, CombinationMembershipType, RateBundlesCollection } from '../types/combinations.types';


const createLabelGroup = (
  item: BecomeAMemberCombinationTypes,
  flowType: CombinationFlowTypes
): CombinationLabelGroup => {
  const labelGroup = {
    sys: item.club?.sys,
    identifier: item.club?.clubId ?? '',
    membershipTypes: [],
  };

  if (
    item.showInExtensionFlow &&
    flowType === CombinationFlowTypes.ShowInExtensionFlow
  ) {
    return {
      ...labelGroup,
      defaultCombination: item.extensionFlowDefaultCombination,
      showInExtensionFlow: item.showInExtensionFlow,
    };
  } else {
    return {
      ...labelGroup,
      defaultCombination: item.defaultCombination,
      showInExtensionFlow: item.showInSalesFlow,
    };
  }
};

const createMembershipType = (
  item: BecomeAMemberCombinationTypes,
  flowType: CombinationFlowTypes
): CombinationMembershipType => {
  const membershipType = {
    sys: item.membershipType?.sys,
    title: item.membershipType?.title,
    description: item.membershipType?.description,
    accessLevels: [],
  };

  if (
    item.showInExtensionFlow &&
    flowType === CombinationFlowTypes.ShowInExtensionFlow
  ) {
    return {
      ...membershipType,
      isDefaultCombination: item.extensionFlowDefaultCombination || false,
    };
  } else {
    return {
      ...membershipType,
      isDefaultCombination: item.defaultCombination || false,
    };
  }
};

const createAccessLevel = (
  item: BecomeAMemberCombinationTypes,
  flowType: CombinationFlowTypes
): CombinationAccessLevel => {
  const accessLevel = {
    sys: item.accessLevel?.sys,
    description: item.accessLevel?.description,
    excludedBenefitsCollection: item.accessLevel?.excludedBenefitsCollection,
    includedBenefitsCollection: item.accessLevel?.includedBenefitsCollection,
    highlightedBenefit: item.accessLevel?.highlightedBenefit,
    title: item.accessLevel?.title,
    voucherCodes: [],
  };

  if (
    item.showInExtensionFlow &&
    flowType === CombinationFlowTypes.ShowInExtensionFlow
  ) {
    const hasExtensionVoucherCodes = Boolean(
      (item.extensionFlowVoucherCodeCollection &&
        item.extensionFlowVoucherCodeCollection?.items?.length > 0) ||
        (item.extensionFlowVoucherGroupCollection &&
          item.extensionFlowVoucherGroupCollection?.items.length > 0)
    );
    return {
      ...accessLevel,
      rateBundles:
        hasExtensionVoucherCodes || !item.rateBundleId
          ? []
          : [
              {
                rateBundleId: item.rateBundleId,
                paymentFrequency: item.paymentFrequency,
                isDefaultCombination: item.extensionFlowDefaultCombination,
                discountCalculatorConfig: item.discountCalculatorConfig,
              },
            ],
      isDefaultCombination: item.extensionFlowDefaultCombination || false,
    };
  } else {
    const hasVoucherCodes = Boolean(
      item.voucherCodeCollection?.items?.length ||
        item.voucherGroupCollection?.items.length
    );
    return {
      ...accessLevel,
      rateBundles:
        hasVoucherCodes || !item.rateBundleId
          ? []
          : [
              {
                rateBundleId: item.rateBundleId,
                paymentFrequency: item.paymentFrequency,
                isDefaultCombination: item.defaultCombination,
                discountCalculatorConfig: item.discountCalculatorConfig,
              },
            ],
      isDefaultCombination: item.defaultCombination || false,
    };
  }
};

const addVoucherCodes = async (
  accessLevel: CombinationAccessLevel,
  item: BecomeAMemberCombinationTypes,
  lang: Languages,
  flowType: CombinationFlowTypes
) => {
  let voucherCodes = item.voucherCodeCollection?.items || [];
  let voucherGroup = item.voucherGroupCollection?.items || [];

  if (
    item.showInSalesFlow &&
    flowType === CombinationFlowTypes.ShowInSalesFlow
  ) {
    voucherCodes = item.voucherCodeCollection?.items || [];
    voucherGroup = item.voucherGroupCollection?.items || [];
  }

  if (
    item.showInExtensionFlow &&
    flowType === CombinationFlowTypes.ShowInExtensionFlow
  ) {
    voucherCodes = item.extensionFlowVoucherCodeCollection?.items || [];
    voucherGroup = item.extensionFlowVoucherGroupCollection?.items || [];
  }

  const voucherCategories =
    voucherGroup.length > 0
      ? voucherGroup.map((voucher) => voucher?.categoryTitle)
      : [];

  const vouchers = [...voucherCodes];

  try {
    if (voucherCategories.length > 0) {
      const vouchersResponse =
        (await fetchContentfulContentModelData<BecomeAMemberVoucherCategory[]>({
          contentType: 'becomeAMemberVoucherCategory',
          field: BecomeAMemberVoucherCategoryFragment,
          where: {
            categoryTitle_in: voucherCategories,
          },
          lang,
          limit: 25,
        })) ?? [];

      vouchersResponse.forEach((voucher) => {
        if (voucher?.voucherCodesCollection?.items?.length) {
          vouchers.push(...voucher.voucherCodesCollection.items);
        }
      });
    }
  } catch (error) {
    console.error('Error while fetching vouchers', error);
  }

  const defaultCombination =
    item.showInExtensionFlow && flowType === CombinationFlowTypes.ShowInExtensionFlow
      ? item.extensionFlowDefaultCombination
      : item.defaultCombination;
  const rateBundleEntry: RateBundlesCollection = {
    rateBundleId: item.rateBundleId ?? null,
    paymentFrequency: item.paymentFrequency,
    isDefaultCombination: defaultCombination ?? null,
    discountCalculatorConfig: item.discountCalculatorConfig,
  };

  if (vouchers.length > 0) {
    if (!accessLevel.voucherCodes) {
      accessLevel.voucherCodes = [];
    }

    const existingVoucherIds = new Set(
      accessLevel?.voucherCodes?.map((v) => v?.sys?.id)
    );

    vouchers.forEach((voucherCodeItem) => {
      const id = voucherCodeItem?.sys?.id;
      if (!id) return;

      if (existingVoucherIds.has(id)) {
        const matchingVoucherCode = accessLevel.voucherCodes?.find(
          (rb) => rb?.sys?.id === id
        );
        if (
          (item.showInExtensionFlow &&
            flowType === CombinationFlowTypes.ShowInExtensionFlow) ||
          (item.showInSalesFlow &&
            flowType === CombinationFlowTypes.ShowInSalesFlow)
        ) {
          accessLevel.isDefaultCombination = defaultCombination ?? false;
        }
        if (matchingVoucherCode?.rateBundles) {
          matchingVoucherCode.rateBundles.push(rateBundleEntry);
        } else {
          matchingVoucherCode!.rateBundles = [rateBundleEntry];
        }
      } else {
        accessLevel?.voucherCodes?.push({
          ...voucherCodeItem,
          rateBundles: [rateBundleEntry],
        });
      }
      existingVoucherIds.add(id);
    });
  }
};

const sortAccessLevels = (
  membershipType: CombinationMembershipType,
  accessLevelOrderCollection?: Maybe<AccessLevel>[]
) => {
  if (!accessLevelOrderCollection) return;

  membershipType.accessLevels.sort((a, b) => {
    const indexA = accessLevelOrderCollection.findIndex(
      (orderItem) => orderItem?.sys?.id === a?.sys?.id
    );
    const indexB = accessLevelOrderCollection.findIndex(
      (orderItem) => orderItem?.sys?.id === b?.sys?.id
    );
    return indexA - indexB;
  });
};

const sortMembershipTypes = (
  labelGroup: CombinationLabelGroup,
  membershipTypeOrderCollection?: Maybe<MembershipType>[]
) => {
  if (!membershipTypeOrderCollection) return;

  labelGroup.membershipTypes.sort((a, b) => {
    const indexA = membershipTypeOrderCollection.findIndex(
      (orderItem) => orderItem?.sys?.id === a?.sys?.id
    );
    const indexB = membershipTypeOrderCollection.findIndex(
      (orderItem) => orderItem?.sys?.id === b?.sys?.id
    );
    return indexA - indexB;
  });
};

export default async function fetchBecomeAMemberCombinations({
  lang,
  id,
  accessLevelOrderCollection,
  membershipTypeOrderCollection,
  flow,
}: BecomeAMemberCombinationFetchingTypes) {
  const response = await fetchContentfulContentModelData<
    BecomeAMemberCombinationTypes[]
  >({
    contentType: 'becomeAMemberCombination',
    field: BecomeAMemberCombination,
    limit: 25,
    where: { club: { clubId: id }, [flow]: true },
    lang,
  });

  const combinations = new Map<string, CombinationLabelGroup>();

  const processingCombinationPromises = response.map(async (item) => {
    if (!item.club?.clubId) {
      console.warn('Missing labelGroup in combination. Skipping item.');
      return;
    }
    const groupId = item.club.clubId;
    let labelGroup = combinations.get(groupId);

    if (!labelGroup) {
      labelGroup = createLabelGroup(item, flow);
      combinations.set(groupId, labelGroup);
    } else if (item.defaultCombination) {
      labelGroup.defaultCombination = true;
    }

    if (!item.membershipType) {
      console.warn(`Missing membershipType in combination: ${item.sys.id}`);
      return;
    }

    const membershipTypeId = item.membershipType.sys.id;
    let membershipType = labelGroup.membershipTypes.find(
      (mt) => mt?.sys?.id === membershipTypeId
    );

    if (!membershipType) {
      membershipType = createMembershipType(item, flow);
      labelGroup.membershipTypes.push(membershipType);
    } else if (
      (item.extensionFlowDefaultCombination &&
        flow === CombinationFlowTypes.ShowInExtensionFlow) ||
      (item.defaultCombination && flow === CombinationFlowTypes.ShowInSalesFlow)
    ) {
      membershipType.isDefaultCombination = true;
    }

    if (!item.accessLevel) {
      console.warn(`Missing accessLevel in combination: ${item.sys.id}`);
      return;
    }

    const accessLevelId = item.accessLevel.sys.id;
    let accessLevel = membershipType.accessLevels.find(
      (al) => al?.sys?.id === accessLevelId
    );

    if (!accessLevel) {
      accessLevel = createAccessLevel(item, flow);
      membershipType.accessLevels.push(accessLevel);
    } else {
      const hasVouchers =
        (item.showInExtensionFlow &&
          flow === CombinationFlowTypes.ShowInExtensionFlow &&
          ((item.extensionFlowVoucherCodeCollection?.items?.length ?? 0) > 0 ||
            (item.extensionFlowVoucherGroupCollection?.items?.length ?? 0) > 0)) ||
        (item.showInSalesFlow &&
          flow === CombinationFlowTypes.ShowInSalesFlow &&
          ((item.voucherCodeCollection?.items?.length ?? 0) > 0 ||
            (item.voucherGroupCollection?.items?.length ?? 0) > 0));
      if (item.rateBundleId && !hasVouchers) {
        if (!accessLevel.rateBundles) {
          accessLevel.rateBundles = [];
        }
        accessLevel.rateBundles.push({
          rateBundleId: item.rateBundleId,
          paymentFrequency: item.paymentFrequency,
          isDefaultCombination:
            flow === CombinationFlowTypes.ShowInExtensionFlow
              ? item.extensionFlowDefaultCombination
              : item.defaultCombination,
          discountCalculatorConfig: item.discountCalculatorConfig,
        });
      }
      if (
        (item.extensionFlowDefaultCombination &&
          flow === CombinationFlowTypes.ShowInExtensionFlow) ||
        (item.defaultCombination && flow === CombinationFlowTypes.ShowInSalesFlow)
      ) {
        accessLevel.isDefaultCombination = true;
      }
    }

    await addVoucherCodes(accessLevel, item, lang, flow);
  });

  await Promise.all(processingCombinationPromises);

  // Apply sorting if order collections are provided
  combinations.forEach((labelGroup) => {
    labelGroup.membershipTypes.forEach((membershipType) => {
      sortAccessLevels(membershipType, accessLevelOrderCollection);
    });
    sortMembershipTypes(labelGroup, membershipTypeOrderCollection);
  });

  return {
    labelGroups: Array.from(combinations.values()),
  };
}
